import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, finalize, Observable, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { FileUploadResponse } from 'src/app/core/models/file-upload-results.model';
import {
  AddScanToULDRequest,
  CloseULORequest,
  CloseUloResponse,
  GetGleLoadsRequest,
  GetGleLoadsResponse,
  GetOutboundGLEResponse,
  OpenULORequest,
  OpenULOResponse,
} from 'src/app/core/models/position-operations/outbound-gle.model';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { environment } from 'src/app/environments/environment';
import { ErrorhandleService } from '../../error-handling/error-handle/errorhandle.service';

@Injectable({
  providedIn: 'root',
})
export class OutboundGLEService {
  private readonly _http = inject(HttpClient);
  private readonly _commonUtils = inject(CommonUtils);
  private readonly _errorHandler = inject(ErrorhandleService);

  readonly baseUrl = environment.apiUrl;

  /**
   * Fetches the list of open ULOs.
   * @returns An observable of GetOutboundGLEResponse.
   */
  getOpenULOs(): Observable<GetOutboundGLEResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<GetOutboundGLEResponse>(this.baseUrl.concat('/outbound/open-ulos/'))
      .pipe(
        take(1),
        catchError((error) => {
          if (error.error.code === 400 && error.error.message === null) {
            this._commonUtils.updateBannerType(BANNER_TYPE.NO_DATA_AVAILABLE);
            return of({} as GetOutboundGLEResponse);
          } else {
            return this._errorHandler.handleError<GetOutboundGLEResponse>(
              error,
              error.error.code,
              'Outbound GLE Service: open ULO',
              BANNER_TYPE.API_400_ERROR,
            );
          }
        }),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }
  /**
   * Fetches the list of open ULOs.
   * @returns An observable of GetOutboundGLEResponse.
   */
  postTransferULOs(payload: any): Observable<any> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<any>(this.baseUrl.concat('/outbound/transfer-ulo'), payload)
      .pipe(
        take(1),
        catchError((error) => {
          if (error?.error?.errors?.[0]?.message) {
            this._commonUtils.updateBannerType400Error(
              error.error.errors[0].message,
            );
            return of({} as GetOutboundGLEResponse);
          } else {
            return this._errorHandler.handleError<GetOutboundGLEResponse>(
              error,
              error.error.code,
              'Outbound GLE Service: open ULO',
              BANNER_TYPE.API_400_ERROR,
            );
          }
        }),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Fetches GLE loads based on the provided request parameters.
   * @param request The request object containing sortCode, sortDate, bay, and belt.
   * @returns An observable of GetGleLoadsResponse.
   */
  getGleLoads(request: GetGleLoadsRequest): Observable<GetGleLoadsResponse> {
    const params = new HttpParams({ fromObject: { ...request } });

    this._commonUtils.updateLoadingModal(true);
    return this._http
      .get<GetGleLoadsResponse>(
        this.baseUrl.concat('/carryoverload/get-gle-loads'),
        {
          params,
        },
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<GetGleLoadsResponse>(
            error,
            error.error.code,
            'Outbound GLE Service: Get GLE loads',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Opens a new ULO with the provided request data.
   * @param request The request object containing ULO details.
   * @returns An observable of OpenULOResponse.
   */
  openNewUlo(request: OpenULORequest): Observable<OpenULOResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<OpenULOResponse>(
        this.baseUrl.concat('/outbound/open-new-ulo/'),
        request,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<OpenULOResponse>(
            error,
            error.error.code,
            'Outbound GLE Service: Open New Ulo',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  /**
   * Closes an existing ULO with the provided request data.
   * @param request The request object containing ULO details.
   * @returns An observable of CloseUloResponse.
   */
  closeUlo(request: CloseULORequest): Observable<CloseUloResponse> {
    this._commonUtils.updateLoadingModal(true);
    return this._http
      .post<CloseUloResponse>(
        this.baseUrl.concat('/outbound/ulo/close/'),
        request,
      )
      .pipe(
        take(1),
        catchError((error) =>
          this._errorHandler.handleError<CloseUloResponse>(
            error,
            error.error.code,
            'Outbound GLE Service: Close ULO',
            BANNER_TYPE.API_400_ERROR,
          ),
        ),
        finalize(() => {
          this._commonUtils.updateLoadingModal(false);
        }),
      );
  }

  addScanToULD(request: AddScanToULDRequest): Observable<FileUploadResponse> {
    return this._http
      .post<FileUploadResponse>(
        this.baseUrl.concat('/outbound/ulo/add-scan/'),
        request,
      )
      .pipe(take(1));
  }
}
